import {Action, Module, Mutation} from 'vuex-module-decorators'
import store, {dataFieldsEditModule} from "@/store";
import {MdbsCore} from "@/api/tsclient";
import BaseModule from "@/store/modules/BaseModule";
import InstrumentDto = MdbsCore.InstrumentDto;
import IInstrumentCreateDto = MdbsCore.IInstrumentCreateDto;
import IInstrumentUpdateDto = MdbsCore.IInstrumentUpdateDto;
import InstrumentUpdateDto = MdbsCore.InstrumentUpdateDto;
import InstrumentCreateDto = MdbsCore.InstrumentCreateDto;
import ITemporaryInstrumentResponseDto = MdbsCore.ITemporaryInstrumentResponseDto;
import TemporaryInstrumentResponseDto = MdbsCore.TemporaryInstrumentResponseDto;
import IInstrumentDataFieldCreateDto = MdbsCore.IInstrumentDataFieldCreateDto;
import InstrumentDataFieldCreateDto = MdbsCore.InstrumentDataFieldCreateDto;
import IInstrumentDataFieldUpdateDto = MdbsCore.IInstrumentDataFieldUpdateDto;
import InstrumentDataFieldUpdateDto = MdbsCore.InstrumentDataFieldUpdateDto;
import InstrumentDataFieldDeleteDto = MdbsCore.InstrumentDataFieldDeleteDto;
import {AEREPORTS} from "@/components/study/studyDefinitionIds";
import InstrumentDeleteDto = MdbsCore.InstrumentDeleteDto;
import AuditReasonOfChangeDto = MdbsCore.AuditReasonOfChangeDto;


const instrumentClient = new MdbsCore.InstrumentClient();

@Module({namespaced: true, name: 'instrument', store: store})
export default class InstrumentModule extends BaseModule {
    instrument: InstrumentDto = new InstrumentDto();
    tempInstrument: ITemporaryInstrumentResponseDto = new TemporaryInstrumentResponseDto();

    @Mutation
    setInstrument(instrument: InstrumentDto) {
        this.instrument = instrument;
    }

    @Mutation
    setTempInstrument(tempInstrument: ITemporaryInstrumentResponseDto) {
        this.tempInstrument = tempInstrument;
    }

    @Mutation
    reset() {
        this.instrument = new InstrumentDto();
    }

    hasValidationMessages() {
        return (this.instrument?.validationMessages?.length ?? 0) > 0
    }

    @Action({rawError: true})
    async loadById(id: string) {
         await this.execute(async () => this.setInstrument(await instrumentClient.getById(id))
        ).catch(() => this.reset());
    }

    @Action({rawError: true})
    async loadTempInstrument(payload: { patientId: string, visitId: string, instrumentDefinitionId: string }) {
        await this.execute(async () => this.setTempInstrument(
            await instrumentClient.getTemporaryInstrumentDataFields(payload.patientId, payload.visitId, payload.instrumentDefinitionId))
        ).catch(() => this.reset());
    }

    @Action({rawError: true})
    async create(instrument: IInstrumentCreateDto) {
        await this.execute(async () => {
            const createdInstrument = await instrumentClient.create(new InstrumentCreateDto(instrument))
            this.setInstrument(createdInstrument)

            if (createdInstrument.definitionId == AEREPORTS.ID) {
                const field = createdInstrument.fields?.traverse(f => f.childFields ?? [])
                    .find(f => f.definitionId == "6e3d8c0f-ebbc-4190-95dd-978f002d379a");
                dataFieldsEditModule.setLastEditedFieldId(field?.id ?? "");
            }
        }).catch();
    }

    @Action({rawError: true})
    async update(instrument: IInstrumentUpdateDto) {
        await this.execute(async () => {
            const updatedInstrument = await instrumentClient.update(instrument.id, new InstrumentUpdateDto(instrument));
            this.setInstrument(updatedInstrument)
        }).catch();
    }

    @Action({rawError: true})
    async createDataField(dataField: IInstrumentDataFieldCreateDto) {
        await this.execute(async () => {
            const instrument = await instrumentClient.createDataField(new InstrumentDataFieldCreateDto(dataField))
            this.setInstrument(instrument);

            if (instrument.canSave) {
                // save the last edited field id to enable reopening of list entries. we have to find the field again
                // in the saved instrument (by definition id and order) because its id changed
                const newField = instrument.fields
                    ?.traverse(field => field.childFields ?? [])
                    .find(field => field.definitionId == dataField.definitionId && field.order == dataField.order);

                // not found via aeEntry if invalid data after save for existing instrument
                // using old id shows old field, but not the new messages
                dataFieldsEditModule.setLastEditedFieldId(newField?.id ?? "");
            }

        }).catch();
    }

    @Action({rawError: true})
    async updateDataField(dataField: IInstrumentDataFieldUpdateDto) {
        await this.execute(async () => {
            const instrument = await instrumentClient.updateDataField(dataField.id, new InstrumentDataFieldUpdateDto(dataField));
            this.setInstrument(instrument)
        }).catch();
    }

    @Action({rawError: true})
    async deleteDataField(payload: {id: string, reasonOfChange: AuditReasonOfChangeDto | undefined}) {
        await this.execute(async () => {
            const dto = new InstrumentDataFieldDeleteDto( {id: payload.id, reasonOfChange: payload.reasonOfChange});
            const instrument = await instrumentClient.deleteDataField(payload.id, dto);
            this.setInstrument(instrument)
        }).catch();
    }

    @Action({rawError: true})
    async delete(payload: {id: string, reasonOfChange: AuditReasonOfChangeDto | undefined}) {
        await this.execute(async () => {
            const dto = new InstrumentDeleteDto( {id: payload.id, reasonOfChange: payload.reasonOfChange});
            await instrumentClient.delete(payload.id, dto);
            this.reset()
        }).catch();
    }
}
